import { ReactNode } from 'react';
import { HeaderTypes } from '../components';
import { ReceiptType, UnitOfMeasureCategoryType } from './receiptTypes';

export type ServiceDocumentType =
  | 'PackingSlipDocumentFee'
  | 'InternationalDocumentFee'
  | 'BolDocumentFee'
  | 'CartonLabelDocumentFee'
  | 'ItemLabelDocumentFee'
  | 'SpecialInstructionsDocumentFee'
  | 'PurchaseOrderDocument'
  | 'ReceivingDocumentation';

export type FeeType =
  | 'ShipmentMarkupFee'
  | 'ShippingFee'
  | 'MaterialFee'
  | 'OrderFee'
  | 'CustomerFee'
  | 'PalletFee'
  | 'CarrierFee'
  | 'CarrierPercentageFee'
  | 'SpecialFee'
  | 'DayFee'
  | 'BrokerFee'
  | 'PickFee'
  | 'ItemUnitOfMeasurePickFee'
  | 'DrayageFee'
  | 'FlatFee'
  | 'HourlyFee'
  | 'SpecialOrderFee'
  | 'SpecialHandlingFee'
  | 'SpecialMaterialFee'
  | 'OrderDocumentFee'
  | 'OrderCreationFee'
  | 'CancelOrderFee'
  | 'SpecialShipmentFee'
  /* cspell: disable-next-line */
  | 'DispatchHandloadFee'
  /**
   * Generated on order processing
   */
  | 'SerialNumberHandlingFee'
  | 'RefreshOrderFee'
  | 'BolFee'
  | 'TruckingJobScheduleFee'
  | 'AdjustmentFee'
  | 'BaseFee'
  | 'FuelFee'
  | 'ChassisFee'
  | 'CustomerBaseFee'
  | 'CustomerFuelFee'
  | 'CustomerChassisFee'
  | 'CustomerFlatFee'
  | 'CustomerHourlyFee'
  | 'CustomerDayFee'
  | 'VisaProcessingFee'
  | 'MastercardProcessingFee'
  | 'AmexProcessingFee'
  | 'DiscoverProcessingFee'
  | 'DinersProcessingFee'
  | 'JcbProcessingFee'
  | 'TerminalFee'
  | 'CustomerTerminalFee'
  | 'OversizedContainerFee'
  | 'CustomerOversizedContainerFee'
  | 'CarrierStandardFee'
  | 'CustomerCarrierStandardFee'
  | 'CarrierHazmatFee'
  | 'CustomerCarrierHazmatFee'
  | 'ManualFee'
  | 'StorageFee'
  | 'AppointmentNonCompliance'
  | 'ReceiptCreationFee'
  | 'PalletLoadedInboundFee'
  | 'FloorLoadedInboundFee'
  | 'SmallParcelInboundFee'
  | 'ExcessPackageItemFee'
  | 'SortingItemsFee'
  | 'UnknownItemFee'
  | 'NonCompliantItemFee'
  | 'LotNumberTrackingFee'
  | 'ExpirationDateTrackingFee'
  | 'SerialNumberTrackingFee'
  | 'WarehouseStorageFee'
  | 'CustomerWarehouseMonthlyFee'
  | 'CustomerIntegrationFee'
  | 'MaterialStorageFee'
  | 'CubicFeetFee'
  | 'MinimumMonthlyHandlingFee'
  | 'MinimumMonthlyStorageFee'
  | 'MinimumMonthlyCombinedHandlingStorageFee'
  | 'ReturnDisposalFee'
  | 'ItemDisposalFee'
  | 'ReturnItemDisposalFee'
  | 'ReturnInspectionFee';

export type CreditCardProcessingFeeType = Extract<
  FeeType,
  | 'VisaProcessingFee'
  | 'MastercardProcessingFee'
  | 'AmexProcessingFee'
  | 'DiscoverProcessingFee'
  | 'DinersProcessingFee'
  | 'JcbProcessingFee'
>;

export type DocumentFeeType =
  | 'PackingSlipDocumentFee'
  | 'InternationalDocumentFee'
  | 'BolDocumentFee'
  | 'CartonLabelDocumentFee'
  | 'ItemLabelDocumentFee'
  | 'SpecialInstructionsDocumentFee';

export type FeeCategoryType =
  | 'Handling'
  | 'Material'
  | 'Shipping'
  | 'Document'
  | 'Special'
  | 'Storage';

export type AppliedFeeEntityType =
  | 'None'
  | 'Order'
  | 'ShipmentJobShipment'
  | 'OrderShipment'
  | 'PackingMaterial'
  | 'OrderPick'
  | 'SpecialCharge'
  | 'OrderDocument'
  | 'Pallet'
  | 'DispatchHandload'
  | 'PhysicalItemSerialNumber'
  | 'Bol'
  | 'ScheduleTrucking'
  | 'DrayageJobShipment'
  | 'DrayageJobProcessing'
  | 'ShipmentJobProcessing'
  | 'ResellerJobShipment'
  | 'SystemService'
  | 'LabelPurchase'
  | 'ReceiptShipment'
  | 'Receipt'
  | 'Appointment';

export enum LinkFeeTo {
  None = 'None',
  Order = 'Order',
  ShippingJob = 'ShippingJob',
  OrderAndShippingJob = 'OrderAndShippingJob',
  DrayageJob = 'DrayageJob',
  Appointment = 'Appointment',
  Receipt = 'Receipt',
  OrderAndReceipt = 'OrderAndReceipt',
}

export type DiscountedRateType = 'ExcessPackages' | 'ExcessItems';

export type ReturnType = 'Receipt' | 'Order';

export interface IdNamePair<TValue = number | string> {
  id: TValue;
  name: string;
  accountNumber?: string | null;
  code?: string | null;
  services?: [];
}

export interface PagedResults<T> {
  results: Array<T>;
  numberOfRows: number;
}

export interface SelectOption<T = string | number> {
  id: T;
  name: string | ReactNode;
  enumInt?: number;
  enumTechValue?: string;
}

export interface AggregatedServiceFeeData {
  totalMarkupPrice: number;
  totalCost: number;
  totalCustomerAmount: number;
  totalProfit: number;
  totalCreditAmount: number;
  totalBrokerFee: number;
  materialFeeCost?: number;
  materialFeeProfit?: number;
  totalProcessorExpense?: number;
  shippingFeeCost?: number;
  shippingFeeProfit?: number;
  orderFees?: number;
  handlingFees?: number;
  storageFees?: number;
}

export interface WeightObject {
  pounds?: number;
  ounces?: number;
  ouncesPercent?: string;
}

export interface DocumentObject {
  id: number;
  name: string;
  type: string;
  objectName: string;
  url: string;
  description: string;
  createdOn: string;
  visibleToCustomer: boolean;
}

export interface RouteIdParamsTypes {
  ORDER_ID?: string;
  TRUCKING_JOB_ID?: string;
  DRAYAGE_JOB_ID?: string;
  FULFILLED_SERVICE_ID?: string;
  RECEIPT_ID?: string;
  APPOINTMENT_ID?: string;
  WAREHOUSE_ID?: string;
  PICK_JOB_ID?: string;
}

export interface BaseDocument {
  id: number;
  document: DocumentObject;
  documentType: ServiceDocumentType;
}

export enum RadioInputEntryModes {
  EXISTING = 'existing',
  MANUAL = 'manual',
}

export interface FeeAPI {
  id?: number;
  name?: string;
  type: FeeType;
  feeTypeDisplay?: string;
  amount?: number;
  percentage?: number;
  min?: number;
  max?: number;
  documentFeeType?: DocumentFeeType;
  documentFeeTypeDisplay?: string;
  linkedToFeeID?: number;
  category?: FeeCategoryType;
  generateExcessFee?: boolean;
  discountedRates?: Array<DiscountedRate>;
}

export interface DiscountedRate {
  id?: number | null;
  feeID: number;
  quantity?: number;
  unitOfMeasureCategory?: UnitOfMeasureCategoryType;
  unitOfMeasureDisplay?: string;
  oversizedWeight?: number;
  oversizedDimension?: number;
  rate?: number;
  appliedReason?: string;
  discountedRateType?: DiscountedRateType;
  discountedRateTypeDisplay?: string;
  receiptType?: ReceiptType;
}

export type AppliedFeesViewResponse = {
  id?: number;
  name?: string | null;
  type?: FeeType;
  feeCategory?: FeeCategoryType;
  documentFeeType?: DocumentFeeType;
  amount?: number;
  entityType?: AppliedFeeEntityType;
  orderID?: number | null;
  shipmentJobID?: number | null;
  drayageJobID?: number | null;
  receiptID?: number | null;
  appointmentID?: number | null;
  customerID?: number | null;
  customerName?: string | null;
  quantity?: number | null;
  baseCost?: number | null;
  shipmentBridgeID?: string | null;
  billableToCustomer?: boolean;
  createdOn?: string;
  isInvoiced?: boolean;
  invoiceID?: number | null;
  note?: string | null;
  discountedRates?: Array<DiscountedRate>;
};

export interface CustomerFeeIdNamePair {
  id: number;
  name: string;
  amount?: number;
  percent?: number;
  defaultFeeId?: number;
  feeType: FeeType;
}

export interface FeeTemplateAPI {
  id?: number;
  name: string;
  type: FeeType | '';
  amount?: number;
  feeTypeDisplay?: string;
}

export interface GroupedCustomerSpecialFeesBasic {
  SpecialOrderFee?: Array<CustomerFeeIdNamePair>;
  SpecialHandlingFee?: Array<CustomerFeeIdNamePair>;
  SpecialMaterialFee?: Array<CustomerFeeIdNamePair>;
  SpecialItemFee?: Array<CustomerFeeIdNamePair>;
  SpecialShipmentFee?: Array<CustomerFeeIdNamePair>;
}

export interface ValidationMessages {
  messages: Array<ValidationMessageType>;
}

export interface ValidationMessageType {
  message: string;
  code: string;
  timeStamp: Date | string;
  level: string;
}

export type ExportParams<T> = {
  exportURLPath: string;
  exportURLQuery?: { [key: string]: string | number | boolean };
  exportFields: Array<HeaderTypes<T>>;
  exportFileName: string;
};

export interface NoteAPI {
  id?: number;
  text: string;
  createdOn: string;
  createdBy?: number;
  createdByFirstName?: string;
  createdByLastName?: string;
  createdByFullName?: string;
}

export type InventoryTrackPreferenceType =
  | 'EnableOnInbound'
  | 'RequireOnInbound'
  | 'RequireOnInboundAndOutbound'
  | 'EnableOnInboundRequireOnOutbound'
  /**
   * For serial number only. They can require it even if we don't track it in inventory.
   */
  | 'EnableOnOutbound'
  /**
   * Explicitly do not track this field for this item, overriding customer defaults.
   */
  | 'DoNotTrack';
